import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, LayoutGrid, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS,
  WORKSPACE_KPI_DEFS,
  type WorkspaceKpiKey,
} from '@/components/workspace/WorkspaceKpiSummary';
import { cn } from '@/utils/cn';

function toggleKey(keys: WorkspaceKpiKey[], key: WorkspaceKpiKey, checked: boolean): WorkspaceKpiKey[] {
  if (checked) {
    if (keys.includes(key)) return keys;
    // Keep canonical metric order from WORKSPACE_KPI_DEFS.
    const next = new Set([...keys, key]);
    return WORKSPACE_KPI_DEFS.filter((def) => next.has(def.key)).map((def) => def.key);
  }
  return keys.filter((item) => item !== key);
}

export function WorkspaceCustomizeMetricsPopover({
  selectedKeys,
  onApply,
}: {
  selectedKeys: readonly WorkspaceKpiKey[];
  onApply: (next: WorkspaceKpiKey[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftKeys, setDraftKeys] = useState<WorkspaceKpiKey[]>([...selectedKeys]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraftKeys([...selectedKeys]);
    setQuery('');
  }, [open, selectedKeys]);

  const filteredDefs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return WORKSPACE_KPI_DEFS;
    return WORKSPACE_KPI_DEFS.filter(
      (def) => def.label.toLowerCase().includes(q) || def.key.toLowerCase().includes(q)
    );
  }, [query]);

  const metricColumns = useMemo(() => {
    const mid = Math.ceil(filteredDefs.length / 2);
    return [filteredDefs.slice(0, mid), filteredDefs.slice(mid)] as const;
  }, [filteredDefs]);

  const draftSet = useMemo(() => new Set(draftKeys), [draftKeys]);
  const selectedCount = draftKeys.length;
  const totalCount = WORKSPACE_KPI_DEFS.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setDraftKeys([...selectedKeys]);
      setQuery('');
    }
  };

  const handleCancel = () => {
    setDraftKeys([...selectedKeys]);
    setQuery('');
    setOpen(false);
  };

  const handleApply = () => {
    onApply(draftKeys);
    setOpen(false);
  };

  const handleReset = () => {
    setDraftKeys([...DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS]);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          className="inline-flex h-8 items-center gap-2 rounded-pill border border-border bg-surface px-3 text-body-sm text-foreground hover:border-brand"
        >
          <LayoutGrid className="h-4 w-4" aria-hidden />
          Customize metrics
          {open ? (
            <ChevronUp className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="z-50 w-[min(22.5rem,calc(100vw-2rem))] p-0 shadow-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5">
          <p className="text-sm font-semibold text-foreground">Select metrics ({totalCount})</p>
          <button
            type="button"
            onClick={handleReset}
            className="text-sm font-medium text-brand hover:text-brand-hover"
          >
            Reset
          </button>
        </div>

        <div className="space-y-3 px-3 py-3">
          <label className="relative block">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground-muted"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search metrics..."
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-foreground placeholder:text-foreground-subtle focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30"
            />
          </label>

          <div
            className="grid max-h-64 grid-cols-2 gap-x-3 gap-y-1.5 overflow-y-auto"
            role="group"
            aria-label="Performance summary metrics"
          >
            {metricColumns.map((column, columnIndex) => (
              <div key={columnIndex} className="min-w-0 space-y-1.5">
                {column.map((def) => {
                  const checked = draftSet.has(def.key);
                  return (
                    <label
                      key={def.key}
                      className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 text-sm text-foreground hover:bg-surface-muted"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) =>
                          setDraftKeys(toggleKey(draftKeys, def.key, event.target.checked))
                        }
                        className="h-3.5 w-3.5 shrink-0 rounded border-border text-brand focus:ring-brand/30"
                      />
                      <span className="truncate">{def.label}</span>
                    </label>
                  );
                })}
              </div>
            ))}
            {filteredDefs.length === 0 ? (
              <p className="col-span-2 py-2 text-center text-sm text-foreground-muted">
                No metrics match “{query.trim()}”.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
          <p className="text-xs text-foreground-muted">
            {selectedCount} of {totalCount} metrics shown
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className={cn(
                'rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground',
                'hover:bg-surface-muted'
              )}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={selectedCount === 0}
              className="rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground hover:bg-brand-hover disabled:opacity-60"
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
